/* eslint-disable no-console */
import React from 'react';
import XLSX from 'xlsx';
import { doSwaggerCall } from './useApi';

export const EXCEL_EXPORT_VARIANTS = {
    PAYMENTS: 'payments',
    PARKINGS: 'parkings',
    USERS: 'users',
};

const exportVariants = {
    [EXCEL_EXPORT_VARIANTS.PAYMENTS]: {
        sheetName: 'Fizetesek',
        header: [
            'Befizetés ID',
            'Összeg (Ft)',
            'Email',
            'Felhasználó ID',
            'Parkolójegy ID',
            'Státusz',
            'Fizette',
        ],
        filenamePrefix: 'fizetesek',
        api: 'Payments',
        operation: 'getPayments',
        apiResponseKey: 'payments',
        mapper: (payment) => [
            payment.orderref || '-',
            payment.amount || '-',
            payment.email || '-',
            payment.user_id || '-',
            payment.ticket_id || '-',
            payment.status || '-',
            payment.byAdmin
                ? `Admin, nem a felhasználó (${
                      payment.byAdmin.name || payment.byAdmin.email || payment.byAdmin.id
                  }) – elengedve`
                : 'Felhasználó',
        ],
    },
    [EXCEL_EXPORT_VARIANTS.PARKINGS]: {
        sheetName: 'Parkolasok',
        header: [
            'Parkolójegy ID',
            'Felhasználó ID',
            'Fizetve',
            'Összeg (Ft)',
            'Státusz',
            'Belépés ideje',
            'Kilépés ideje',
            'Szabadidő belépés után (perc)',
            'Szabadidő fizetés után (perc)',
            'Felhasználó e-mail',
            'Kézzel lezárva',
            'Lezárta',
            'Lezárás oka',
        ],
        filenamePrefix: 'parkolasok',
        api: 'ParkingTickets',
        operation: 'getParkingTickets',
        apiResponseKey: 'tickets',
        mapper: (ticket) => [
            String(ticket.id) || '-',
            ticket.user_id || '-',
            ticket.paid ? 'Igen' : 'Nem',
            ticket.amount || '-',
            ticket.status || '-',
            ticket.entryTime ? ticket.entryTime.replaceAll('-', '.') : '-',
            ticket.exitTime ? ticket.exitTime.replaceAll('-', '.') : '-',
            ticket.freeTimeAfterEntry || '-',
            ticket.freeTimeAfterPayment || '-',
            ticket.userEmail || '-',
            ticket.closedAt ? ticket.closedAt.replaceAll('-', '.') : '-',
            ticket.closedBy ? ticket.closedBy.name || ticket.closedBy.email || '-' : '-',
            ticket.closeComment || '-',
        ],
    },
    [EXCEL_EXPORT_VARIANTS.USERS]: {
        sheetName: 'Felhasznalok',
        header: [
            'Azonosító',
            'Vezetéknév',
            'Keresztnév',
            'E-mail cím',
            'Regisztráció',
            'Születési dátum',
            'Nyelv',
            'Jogosultság',
            'Dolgozói QR',
            'Lurdy Life tag',
        ],
        filenamePrefix: 'felhasznalok',
        api: 'Users',
        operation: 'getUsers',
        apiResponseKey: 'users',
        mapper: (user) => [
            user.id || '-',
            user.lastname || '-',
            user.firstname || '-',
            user.email || '-',
            user.created_at ? user.created_at.replaceAll('-', '.') : '-',
            user.birthdate ? user.birthdate.replaceAll('-', '.') : '-',
            user.lang || '-',
            user.role || '-',
            user.vipurl || '-',
            user.lurdylifemember ? 'Igen' : 'Nem',
        ],
    },
};

const useExcelExport = () => {
    const [isLoading, setIsLoading] = React.useState(false);

    const grabAllData = async ({ api, operation, apiResponseKey, total }) => {
        const PAGE_LIMIT = 100;
        const allData = [];

        if (total <= PAGE_LIMIT) {
            const response = await doSwaggerCall(api, operation, {
                limit: total,
                offset: 0,
            });
            return response[apiResponseKey] || [];
        }

        let offset = 0;
        while (offset < total) {
            // eslint-disable-next-line no-await-in-loop
            const response = await doSwaggerCall(api, operation, {
                limit: PAGE_LIMIT,
                offset,
            });

            allData.push(...(response[apiResponseKey] || []));
            if (!response[apiResponseKey] || response[apiResponseKey].length < PAGE_LIMIT) {
                break;
            }
            offset += PAGE_LIMIT;
        }

        return allData;
    };

    const handleExport = async (total, variant) => {
        if (!exportVariants[variant]) {
            console.error('Invalid export variant:', variant);
            return;
        }
        const { sheetName, header, api, operation, filenamePrefix, apiResponseKey, mapper } =
            exportVariants[variant];

        setIsLoading(true);
        try {
            const allData = await grabAllData({
                api,
                operation,
                total,
                apiResponseKey,
            });
            const workbook = XLSX.utils.book_new();

            const mappedData = allData.map((data) => mapper(data));
            const data = [header, ...mappedData];
            const worksheet = XLSX.utils.aoa_to_sheet(data);
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            // Check Workbook data before writeFile
            if (
                Object.keys(workbook.Sheets).length === 0 ||
                Object.values(workbook.Sheets).every(
                    (sheet) => !sheet || Object.keys(sheet).length === 0
                )
            ) {
                throw new Error('No valid data found for export');
            } else {
                // Generate filename with timestamp
                const timestamp = new Date()
                    .toISOString()
                    .replace(/[-:TZ.]/g, '')
                    .slice(0, 14);
                const filename = `${timestamp}_${filenamePrefix}.xlsx`;
                XLSX.writeFile(workbook, filename);
            }
        } catch (error) {
            console.error('Error exporting payments:', error);
        }
        setIsLoading(false);
    };

    return { isLoading, handleExport };
};

export default useExcelExport;
